<template lang="pug">
  .chat
    .mobile-header
      .close(@click="emit('close')")
        Icon.icon(name="arrow-left" fill="#333")
        div {{__('Live Chat', eventNamespace)}}
    .pinned-message(v-if="pinnedMsg")
      .pinned-header(@click="toggleCollapsePin()")
        Icon.icn(name="pin" fill="white")
        strong {{ __('Pinned Message') }}
        Icon.icn(name="arrow-down" fill="white" v-if="collapsePinned" @click="toggleCollapsePin()")
        Icon.icn(name="arrow-up" fill="white" v-else  @click="toggleCollapsePin()")
      .pinned-content(:class="{ collapsed: collapsePinned }")
        Icon.icn(:name="pinnedMsg.icon" v-if="pinnedMsg.icon")
        strong {{ pinnedMsg.name }}:&nbsp;
        | {{ pinnedMsg.message }}
    .chat-messages(ref="historyRef" v-if="config.state === 'open' || (hasAccess && (config.state === 'live' || config.state === 'moderator'))")
      .msg(v-for="msg in history" :class="msg.status" :key="msg.id")
        Icon.icn(:name="msg.icon" v-if="msg.icon")
        strong {{ msg.name }}:&nbsp;
        | {{ msg.message }}
    .placeholder(v-if="!hasAccess && config.state !== 'open'")
      | {{__('Accessing the Live Chat feature requires purchasing Iron Game Premium.', eventNamespace)}}
      .link(@click="showOverlay('buyPremium')") {{__('Purchase', eventNamespace)}}
    .placeholder(v-else-if="config.state === 'upcoming'")
      | {{__('Chat will activate once the competition begins.', eventNamespace)}}
    .placeholder(v-else-if="config.state === 'ended'")
      | {{__('Chat for this event has ended.', eventNamespace)}}
    spinner.placeholder(v-else-if="config.state === 'loading'")
    .write
      Message.info(v-if="infoMsg") {{__(infoMsg, 'auto')}}
      Message.info(v-if="config.state === 'open' && !loggedIn" link-url="/login" link-text="Log In" ) {{__('You must log in or create an account to participate in chat', eventNamespace)}}
      textarea(
        placeholder="Type your message"
        @keydown.enter="enterKey($event)"
        v-model="newMsg"
        :disabled="!enabled"
        ref="areaRef"
      )
      RogueButton.send-btn(color="black" :disabled="!enabled" width="full" size="s" @click="sendMessage()") {{__('Send', eventNamespace)}}
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, inject, type ComputedRef } from 'vue'
import { eventNamespace } from '../../js/constants'
import { useRogue } from '../../js/rogue'
import { useMQ } from '../../js/mq'


interface ChatMessage {
  id: string | number
  name: string
  message: string
  icon?: string
  status?: string
}

interface ChatPostResponse {
  channel: 'live' | 'pending' | 'denied'
}

const props = withDefaults(defineProps<{
  chatKey: string
  hasTicket: boolean
  hasActiveTrial?: boolean
}>(), {
  hasActiveTrial: false,
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const loggedIn = inject<ComputedRef<boolean>>('loggedIn', computed(() => false))
const loadScript = inject<(args: { script: string; async: boolean }) => Promise<void>>('loadScript')
const showOverlay = inject<(name: string) => void>('showOverlay')
const mq = useMQ()

const rogue = useRogue()
const config = ref({ state: 'loading' })
const infoMsg = ref('')
const newMsg = ref('')
const history = ref<ChatMessage[]>([])
const pinned = ref<ChatMessage[]>([])
const collapsePinned = ref(true)

const historyRef = ref<HTMLElement | null>(null)
const areaRef = ref<HTMLTextAreaElement | null>(null)

// Internal Firebase State
let fb: any = null
let configRef: any = null
let messagesRef: any = null
let pinnedRef: any = null
let firebaseInitialized = false

const hasAccess = computed(() => (config.value.state === 'open' && loggedIn) || props.hasTicket)

const enabled = computed(() => {
  return hasAccess.value && 
         ['live', 'moderator'].includes(config.value.state) && 
         !props.hasActiveTrial
})

const pinnedMsg = computed(() => pinned.value.length ? pinned.value[0] : null)

// --- Methods ---
const scrollToBottom = () => {
  nextTick(() => {
    if (historyRef.value) {
      historyRef.value.scrollTo(0, historyRef.value.scrollHeight)
    }
  })
}

const loadFirebase = async () => {
  if (!showOverlay || typeof window === 'undefined' || fb || !hasAccess.value) {
    return
  }

  if (!hasAccess.value) {
    const state = await rogue.fetch<string>(`https://rflivechat-default-rtdb.firebaseio.com/events/${props.chatKey}/config/state.json`)
    if (state !== 'open') return
    config.value.state = state
  }

  // Load SDKs
  if (!firebaseInitialized && loadScript) {
    await Promise.all([
      loadScript({ script: 'https://www.gstatic.com/firebasejs/8.10.1/firebase-app.js', async: true }),
      loadScript({ script: 'https://www.gstatic.com/firebasejs/8.10.1/firebase-database.js', async: true }),
    ])

    try {
      const firebase = (window as any).firebase
      if (!firebase.apps.length) {
        firebase.initializeApp({
          databaseURL: 'https://rflivechat-default-rtdb.firebaseio.com/',
        })
      }
      firebaseInitialized = true
    } catch (e:any) {
      rogue.log?.error('Firebase Init Error', e)
    }
  }

  fb = (window as any).firebase.database()

  configRef = fb.ref(`events/${props.chatKey}/config/state`)
  configRef.on('value', (snap: any) => config.value.state = snap.val() || 'loading')

  messagesRef = fb.ref(`events/${props.chatKey}/live`)
  messagesRef.on('child_added', (data: any) => {
    history.value.push(data.val())
    scrollToBottom()
  })
  messagesRef.on('child_removed', (data: any) => {
    const val = data.val()
    history.value = history.value.filter(m => m.id !== val.id)
  })

  pinnedRef = fb.ref(`events/${props.chatKey}/pinned`)
  pinnedRef.on('child_added', (data: any) => {
    pinned.value.push(data.val())
    scrollToBottom()
  })
  pinnedRef.on('child_removed', (data: any) => {
    const val = data.val()
    pinned.value = pinned.value.filter(m => m.id !== val.id)
  })
}

const sendMessage = async () => {
  if (!newMsg.value.trim() || !rogue.fetch) return

  try {
    const resp = await rogue.fetch<ChatPostResponse>(
      `/svc_live/events/${props.chatKey}/chat`, 'post',
      { msg: newMsg.value }
    )

    if (resp?.channel === 'pending') {
      infoMsg.value = 'Your message has been sent to the moderator for review.'
      setTimeout(() => infoMsg.value = '', 5000)
    }

    newMsg.value = ''
    if (mq.value === 'mobile' || mq.value === 'tablet') {
      areaRef.value?.blur()
    }
  } catch (e:any) {
    rogue.log?.error('Chat Send Error', e)
  }
}

const enterKey = (e: KeyboardEvent) => {
  if (!e.ctrlKey) {
    e.preventDefault()
    sendMessage()
  } else {
    newMsg.value += '\n'
  }
}

const toggleCollapsePin = () => {
  collapsePinned.value = !collapsePinned.value
}

// --- Watchers & Lifecycle ---
watch(hasAccess, (newVal) => {
  if (newVal) loadFirebase()
})

onMounted(() => {
  nextTick(() => loadFirebase())
})

onBeforeUnmount(() => {
  if (messagesRef) messagesRef.off()
  if (pinnedRef) pinnedRef.off()
  if (configRef) configRef.off()
})
</script>
<style lang="scss" scoped>
.chat {
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-self: stretch;

  max-height: 100%;

  @include mobile() {
    z-index: 15;
    position: relative;
    bottom: 0;
  }
}
.mobile-header {
  background-color: $color-gray-x-light;
  border-bottom: 1px solid $color-gray-dark;
  padding: $space-s;
  align-items: center;
}

.write {
  margin: 0 $space-xs $space-s $space-xs;
}

@include all-desktop(){
  .write {
    margin: 0 0 $space-s 0
  }
  .mobile-header {
    display: none;
  }
  .chat {
    max-height: 450px;
  }
}

.close {
  display: inline-flex;
  font-weight: $weight-bold;

  .icon {
    height: 14px;
  }

  div {
    margin-left: $space-xxs;
  }
}

.placeholder, .chat-messages {
  flex-grow: 1;
  border: 1px solid $color-gray-light;
  border-top: none;
  margin-bottom: $space-xs;

  @include not-desktop() {
    max-height: 70vh;
  }
}

.chat-messages {
  font-size: $size-xxs;
  overflow-y: auto;
}

.pinned-message {
  border: 1px solid $color-gray-light;
  border-top: none;
  font-size: $size-xxs;
}

.placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: $size-s;
  line-height: $line-height-l;
  font-weight: $weight-normal;
  color: $color-gray-dark;
  text-align: center;
  padding: $space-s;

  .link {
    margin-top: $space-s;
  }
}

.msg, .pinned-message {
  padding: $space-xs;
}

.msg.moderator strong {
  color: $color-blue-dark
}

.msg .icn, .pinned-message .icn {
  display: inline-block;
  margin-right: 2px;
  width: 10px;
  height: 10px;
}

.pinned-message {
  background-color: $color-black;
  color: $color-white;
}

.pinned-header {
  cursor: pointer;
  display: flex;
  padding-bottom: $space-xxs;

  .icn {
    width: 12px;
    height: 12px;

    &:last-child {
      margin-left: auto;
    }
  }

  strong {
    vertical-align: middle;
  }
}

.pinned-content.collapsed {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info {
  margin-bottom: $space-s;
}

textarea {
  background-color: $color-gray-x-light;
  width: 100%;
  min-height: 69px;
  padding: $space-xs;
  font-size: $size-xxs;
  border-radius: $radius-default;
  border: 1px solid $color-gray-light;
  resize: none;
  margin-bottom: $space-xxs;
}

</style>
